import _ from 'lodash';
// @mui
import { styled } from '@mui/material/styles';
import { Stack, Box, useTheme, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
//reducers
import { selectTranslations } from 'state/reducers/translation.reducers';
import { useAppSelector } from 'hooks/redux.hooks';
import { StepperCard } from './Cards';

type stepperProps = {
	activeStep: number;
	total: number | object;
	stepsPrefix: string;
};

export default function Stepping(props: stepperProps) {
	const t = useAppSelector(selectTranslations);
	const total = _.keys(props.total).length;
	const theme = useTheme();

	// For digital balance in mobile view, use regular balance keys
	const isDigitalBalance = props.stepsPrefix.includes('digital');
	const fallbackStepsPrefix = isDigitalBalance ? 'page.balance.add.step' : props.stepsPrefix;

	return (
		<Box margin={{ xs: 0 }}>
			<Stack
				sx={{ width: '100%', [theme.breakpoints.down('sm')]: { display: 'none' } }}
				direction="row"
				spacing={0.5}
			>
				{_.keys(props.total).map((label, i) => (
					<Step
						key={label}
						activeStep={props.activeStep}
						step={i}
						startOfSteps={true}
						endOfSteps={true}
						total={total}
					>
						<Status activeStep={props.activeStep} step={i}>
							{i < props.activeStep ? <CheckIcon sx={{ height: '100%' }} /> : i + 1}
						</Status>
						<Box>{t(`${props.stepsPrefix}.${i + 1}.title`)}</Box>
					</Step>
				))}
			</Stack>
			<Box sx={{ [theme.breakpoints.up('sm')]: { display: 'none' } }}>
				<StepperCard>
					<Stack spacing={2} direction={'row'}>
						<RoundProgressBar step={props.activeStep + 1} total={total} />
						<Stack spacing={0} justifyContent={'center'}>
							<Stack direction={'row'} spacing={0.5}>
								<Typography
									variant="boldBody1"
									fontWeight="600"
									color={theme.palette.root.basicDeepBlue}
								>{`${t(fallbackStepsPrefix + '.text')} ${props.activeStep + 1}`}</Typography>
								<Typography>{t(`${props.stepsPrefix}.${props.activeStep + 1}.title`)}</Typography>
							</Stack>
							{props.activeStep + 1 < total && (
								<Stack direction={'row'} spacing={0.5} color={theme.palette.root.textGreen}>
									<Typography variant="semiCaption">
										{t(fallbackStepsPrefix + '_next.text')}
									</Typography>
									<Typography variant="semiCaption">
										({t(`${props.stepsPrefix}.${props.activeStep + 2}.title`)})
									</Typography>
								</Stack>
							)}
						</Stack>
					</Stack>
				</StepperCard>
			</Box>
		</Box>
	);
}

export type StepProps = {
	activeStep: number;
	step: number;
	total: number;
	startOfSteps: boolean;
	endOfSteps: boolean;
};

export const Step = styled('div')<StepProps>((props) => ({
	display: 'flex',
	width: `${Math.round(100 / props.total)}%`,
	height: '48px',
	lineHeight: '48px',
	textAlign: 'center',
	alignItems: 'center',
	fontFamily: 'RubikMedium',
	fontSize: '15px',
	borderTopLeftRadius: props.startOfSteps ? '10px' : 'none',
	borderTopRightRadius: props.endOfSteps ? '10px' : 'none',
	borderBottomLeftRadius: props.startOfSteps ? '10px' : 'none',
	borderBottomRightRadius: props.endOfSteps ? '10px' : 'none',
	color: _.isEqual(props.activeStep, props.step)
		? props.theme.palette.root.basicWhite
		: props.theme.palette.root.basicGreen,
	backgroundColor: _.isEqual(props.activeStep, props.step)
		? props.theme.palette.root.basicDeeoGreen
		: props.activeStep > props.step
		? props.theme.palette.root.brandSecondary_012
		: props.theme.palette.root.grayed,
}));

export const Status = styled('div')<{ activeStep: number; step: number }>((props) => ({
	width: '40px',
	height: '40px',
	borderRadius: '8px',
	lineHeight: '40px',
	textAlign: 'center',
	margin: '0 8px 0 4px',
	fontSize: '20px',
	backgroundColor: _.isEqual(props.activeStep, props.step)
		? props.theme.palette.root.brandSecondary3
		: props.activeStep > props.step
		? props.theme.palette.root.basicWhite
		: props.theme.palette.root.darkerGrayed,
}));

export const RoundProgressBar = (props: { step: number; total: number }) => {
	const { step, total } = props;
	const theme = useTheme();
	const circleCSS = {
		borderRadius: '50%',
		width: '56px',
		height: '56px',
		textAlign: 'center',
	};
	let clip = '0 0, 100% 0, 100% 100%, 0% 100%';
	if (total === 4 && step < 4) {
		clip =
			step === 1
				? '50% 0, 100% 0, 100% 50%, 50% 50%'
				: step === 2
				? '50% 0, 100% 0, 100% 100%, 50% 100%'
				: '50% 0, 100% 0, 100% 100%, 0 100%,0 50%, 50% 50%';
	}
	if (total === 3 && step < 3) {
		clip = step === 1 ? '50% 50%,50% 0%, 100% 0%,100% 75%' : '50% 50%,50% 0%, 100% 0%,100% 100%,0% 100%';
	}

	return (
		<Box
			className="25pie"
			sx={{
				...circleCSS,
				background: '#fff',
				border: '4px solid #CBDCDE',
				position: 'relative',
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				'&:before': {
					...circleCSS,
					border: '4px solid #F75D12',
					position: 'absolute',
					left: '-4px',
					top: '-4px',
					content: '""',
					clipPath: `polygon(${clip})`,
					transition: theme.transitions.create(['clip-path'], {
						duration: theme.transitions.duration.standard,
					}),
					//clip-path: polygon(50% 0, 100% 0, 100% 50%, 50% 50%);  25%
					//clip-path: polygon(50% 0, 100% 0, 100% 100%, 50% 100%); 50%
					//clip-path: polygon(50% 0, 100% 0, 100% 100%, 0 100%,0 50%, 50% 50%); 75%
					//clip-path: polygon(50% 50%,50% 0%, 100% 0%,100% 75%); 33%
					//clip-path: polygon(50% 50%,50% 0%, 100% 0%,100% 100%,0% 100%); 66%
				},
			}}
		>
			<Typography variant="boldBody1" fontWeight="600">
				{step}
			</Typography>
			<Typography variant="semiCaption" lineHeight={1}>
				/{total}
			</Typography>
		</Box>
	);
};
